import { useState, useMemo, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import navbarlogo from '../../../assets/images/brand/navbarlogo.png';
import { useLanguage } from '../../../contexts/LanguageContext';
import vietnamFlag from '../../../assets/images/common/vietnam.webp';
import usFlag from '../../../assets/images/common/us.webp';
import germanyFlag from '../../../assets/images/common/germany.webp';
import japanFlag from '../../../assets/images/common/japan.webp';
import franceFlag from '../../../assets/images/common/france.webp';
import './nav.css';

function Navbar() {
  const location = useLocation();
  const { language, t, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, []);

  const handleNavClick = useCallback((e) => {
    if (location.pathname === '/photo/result') {
      const confirmExit = confirm('Bạn có chắc chắn muốn thoát ra khi chưa lưu ảnh không?');
      if (!confirmExit) {
        e.preventDefault();
        return;
      }
    }
    closeMenu();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, closeMenu]);

  const handleLanguageChange = useCallback((lang) => {
    changeLanguage(lang);
    setLanguageDropdownOpen(false);
  }, [changeLanguage]);

  const navLinks = useMemo(() => [
    { path: '/', label: t('nav.home'), isHome: true },
    { path: '/feedback', label: t('nav.gallery') },
    {
      label: t('nav.guide'),
      subLinks: [
        { path: '/guide/create-frames', label: t('nav.create_frames') },
        { path: '/guide/how-to-use', label: t('nav.how_to_use') }
      ]
    },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ], [t]);

  const languages = useMemo(() => [
    { code: 'eng', label: 'English', flag: usFlag },
    { code: 'vie', label: 'Tiếng Việt', flag: vietnamFlag },
    { code: 'ger', label: 'Deutsch', flag: germanyFlag },
    { code: 'jpn', label: '日本語', flag: japanFlag },
    { code: 'fra', label: 'Français', flag: franceFlag },
  ], []);

  const LanguageOptions = () => (
    <>
      {languages.map((lang) => (
        <button
          key={lang.code}
          className={`lang-option ${language === lang.code ? 'active' : ''}`}
          onClick={() => handleLanguageChange(lang.code)}
        >
          <img src={lang.flag} alt={lang.label} className="lang-flag-icon" />
          <span>{lang.label}</span>
        </button>
      ))}
    </>
  );

  return (
    <header className="main-header">
      <nav className="header-nav">
        <button
          className="mobile-nav-toggle"
          aria-label="Mở menu"
          aria-expanded={isOpen}
          onClick={toggleMenu}
        >
          <span className={`hamburger ${isOpen ? 'open' : ''}`}></span>
        </button>
        <div className="mobile-lang-dropdown-container">
          <button
            className="mobile-lang-toggle"
            onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
            aria-label="Đổi ngôn ngữ"
          >
            <img
              src={languages.find(l => l.code === language)?.flag || usFlag}
              alt={language.toUpperCase()}
              className="lang-flag-icon"
            />
            <span>{language.toUpperCase()}</span>
            <svg className={`dropdown-icon ${languageDropdownOpen ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <div className={`lang-dropdown-menu ${languageDropdownOpen ? 'show' : ''}`}>
            <LanguageOptions />
          </div>
        </div>
        <div className={`nav-links ${isOpen ? 'open' : ''}`}>
          {navLinks.map((link, idx) => {
            if (link.subLinks) {
              return (
                <div
                  key={idx}
                  className="nav-dropdown-container"
                  onMouseEnter={() => window.innerWidth > 768 && setDropdownOpen(true)}
                  onMouseLeave={() => window.innerWidth > 768 && setDropdownOpen(false)}
                >
                  <div
                    className={`header-link dropdown-toggle ${link.subLinks.some(s => location.pathname === s.path) ? 'active' : ''}`}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    {link.label}
                    <svg className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                    {link.subLinks.map(sub => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className={`dropdown-link ${location.pathname === sub.path ? 'active' : ''}`}
                        onClick={handleNavClick}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={link.path}
                to={link.path}
                className={`header-link ${location.pathname === link.path ? 'active' : ''} ${link.isHome ? 'home-link' : ''}`}
                onClick={handleNavClick}
              >
                {link.isHome ? (
                  <>
                    <img src={navbarlogo} alt="Logo" className="nav-logo" />
                    <span>{link.label}</span>
                  </>
                ) : (
                  link.label
                )}
              </Link>
            );
          })}
          <div className="desktop-lang-dropdown-container"
            onMouseEnter={() => setLanguageDropdownOpen(true)}
            onMouseLeave={() => setLanguageDropdownOpen(false)}
          >
            <button
              className="desktop-lang-toggle"
              aria-label="Đổi ngôn ngữ"
            >
              <img
                src={languages.find(l => l.code === language)?.flag || usFlag}
                alt={language.toUpperCase()}
                className="lang-flag-icon"
              />
              <span>{language.toUpperCase()}</span>
              <svg className={`dropdown-icon ${languageDropdownOpen ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div className={`lang-dropdown-menu ${languageDropdownOpen ? 'show' : ''}`}>
              <LanguageOptions />
            </div>
          </div>
        </div>
        <div className={`mobile-nav-overlay ${isOpen ? 'show' : ''}`} onClick={closeMenu}></div>
      </nav>
    </header>
  );
}

export default Navbar;